import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Download, Lock } from "lucide-react";
import { toast } from "sonner";
import PoweredBy from "@/components/PoweredBy";

export default function Certificate() {
  const [info, setInfo] = useState(null);
  const [locked, setLocked] = useState(false);
  const { branding } = useBranding();
  const certRef = useRef(null);

  useEffect(() => {
    api.get("/certificate")
      .then(r => setInfo(r.data))
      .catch(e => { if (e?.response?.status === 403) setLocked(true); });
  }, []);

  const downloadPdf = () => {
    if (!info) return;
    const primary = branding.primary_color || "265 55% 28%";
    const logo = branding.logo_url;
    const logoBlock = logo
      ? `<img src="${logo}" alt="" style="height:110px;width:110px;object-fit:contain;border-radius:9999px;border:2px solid hsl(${primary});" />`
      : `<div style="height:110px;width:110px;border-radius:9999px;background:hsl(${primary});color:#fff;font-family:Georgia,serif;font-size:56px;line-height:110px;">${(info.brand_name || "D")[0]}</div>`;
    // Print-to-PDF via browser
    const win = window.open("", "_blank");
    if (!win) { toast.error("Popup blocked — allow popups to download."); return; }
    win.document.write(`<!doctype html><html><head><title>Certificate - ${info.student_name}</title>
      <style>
        body { font-family: Georgia, serif; margin: 0; padding: 60px; background: #f6f5ee; }
        .cert { border: 2px solid hsl(${primary}); padding: 60px; max-width: 900px; margin: 0 auto; background: #fffdf4; text-align: center; position: relative; }
        .cert::before, .cert::after { content: ''; position: absolute; width: 40px; height: 40px; border: 2px solid hsl(${primary}); }
        .cert::before { top: 12px; left: 12px; border-right: none; border-bottom: none; }
        .cert::after { bottom: 12px; right: 12px; border-left: none; border-top: none; }
        .logo-wrap { display: flex; justify-content: center; margin-bottom: 12px; }
        .brand { letter-spacing: 8px; text-transform: uppercase; font-size: 14px; color: hsl(${primary}); margin-top: 8px; }
        .cert h1 { font-size: 56px; margin: 24px 0 8px; color: hsl(${primary}); font-weight: normal; }
        .cert p { font-size: 18px; color: #333; }
        .name { font-size: 44px; font-style: italic; margin: 20px 0; }
        .meta { margin-top: 40px; font-size: 14px; letter-spacing: 3px; text-transform: uppercase; color: #666; }
        .row { display: flex; justify-content: space-around; margin-top: 40px; }
        .col { border-top: 1px solid #999; padding-top: 8px; width: 240px; font-size: 12px; }
      </style></head><body>
      <div class="cert">
        <div class="logo-wrap">${logoBlock}</div>
        <div class="brand">${info.brand_name}</div>
        <h1>Certificate of Completion</h1>
        <p>This certifies that</p>
        <div class="name">${info.student_name}</div>
        <p>has successfully completed the ${info.brand_name} Tax Course<br/>with a final score of <strong>${info.final_score}%</strong>.</p>
        <div class="row">
          <div class="col">${new Date(info.completion_date).toLocaleDateString()}<br/><span style="font-size:10px;color:#999;">Date</span></div>
          <div class="col">${info.certificate_id}<br/><span style="font-size:10px;color:#999;">Certificate ID</span></div>
        </div>
        <div class="meta">${info.brand_name}</div>
        <div style="margin-top:16px;font-size:9px;letter-spacing:3px;text-transform:uppercase;color:#999;">Powered by <span style="font-family:Georgia,serif;font-style:italic;text-transform:none;letter-spacing:0;">DyNamic Tax</span></div>
      </div>
      <script>window.onload = () => { setTimeout(() => window.print(), 300); }</script>
      </body></html>`);
    win.document.close();
  };

  if (locked) return (
    <Layout>
      <div className="max-w-2xl mx-auto p-12 text-center">
        <Lock size={40} className="mx-auto text-muted-foreground mb-4" />
        <h1 className="font-serif text-4xl mb-3">Certificate locked</h1>
        <p className="text-muted-foreground mb-6">Pass the Final Exam with 80% or higher to unlock your downloadable certificate.</p>
        <Button asChild data-testid="go-exam-btn"><a href="/exam">Take the Final Exam</a></Button>
      </div>
    </Layout>
  );

  if (!info) return <Layout><div className="p-10">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="label-caps mb-2">Your certificate</div>
            <h1 className="font-serif text-4xl lg:text-5xl">Course completed.</h1>
          </div>
          <Button onClick={downloadPdf} data-testid="download-cert-btn" size="lg"><Download size={16} className="mr-2" /> Download PDF</Button>
        </div>

        <div ref={certRef} className="relative border-2 border-primary rounded-md p-10 lg:p-16 bg-card text-center paper-texture" data-testid="certificate-view">
          <span aria-hidden className="absolute top-3 left-3 h-8 w-8 border-l-2 border-t-2 border-primary" />
          <span aria-hidden className="absolute bottom-3 right-3 h-8 w-8 border-r-2 border-b-2 border-primary" />
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-24 w-24 lg:h-28 lg:w-28 object-contain rounded-full border-2 border-primary" data-testid="cert-logo" />
            ) : (
              <div className="h-24 w-24 lg:h-28 lg:w-28 rounded-full bg-primary text-primary-foreground grid place-items-center font-serif text-5xl">
                {(branding.brand_name || info.brand_name || "D")[0]}
              </div>
            )}
          </div>
          <div className="label-caps mb-6" style={{color: "hsl(var(--primary))"}}>{branding.brand_name || info.brand_name}</div>
          <h2 className="font-serif text-5xl lg:text-6xl leading-none mb-6" style={{color: "hsl(var(--primary))"}}>Certificate of Completion</h2>
          <p className="text-muted-foreground mb-4">This certifies that</p>
          <div className="font-serif text-4xl italic mb-6" data-testid="cert-student-name">{info.student_name}</div>
          <p className="max-w-lg mx-auto">has successfully completed the {branding.brand_name || info.brand_name} Tax Course with a final score of <strong>{info.final_score}%</strong>.</p>
          <div className="flex flex-col sm:flex-row justify-around mt-12 gap-6">
            <div className="border-t border-border pt-2 sm:w-56 mx-auto">
              <div className="font-mono text-sm">{new Date(info.completion_date).toLocaleDateString()}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Date</div>
            </div>
            <div className="border-t border-border pt-2 sm:w-56 mx-auto">
              <div className="font-mono text-sm">{info.certificate_id}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Certificate ID</div>
            </div>
          </div>
          <PoweredBy className="mt-10 text-center" />
        </div>
      </div>
    </Layout>
  );
}
